import type { IconProps } from '@carboncopyco/icons';
import { getMaxZIndex } from '@carboncopyco/utils';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Button } from '../../Button';
import { Paper } from '../../Paper';
import { FilterCapsule } from '../FilterCapsule';
import type { DropdownCapsuleState } from '../FilterCapsule';
import type { DropdownFilterItemContent, FilterCommonProps } from '../types';
import { DropdownFilterItem } from './DropdownFilterItem';

export type DropdownFilterProps<T extends string> = {
  items: Array<DropdownFilterItemContent<T>>;
  selected?: T;
  onSelect: (id: T) => void;
  leftIcon?: React.FC<IconProps>;
  onClear?: () => void;
  sideOffset?: number;
  zIndex?: number;
} & FilterCommonProps;

export function DropdownFilter<T extends string>({
  items,
  selected,
  onSelect,
  leftIcon,
  placeholder,
  disabled,
  rounded = true,
  size,
  capsuleWidth,
  contentWidth,
  zIndex,
  onClear,
  sideOffset = 4,
  withoutPortal,
}: DropdownFilterProps<T>) {
  const [capsuleState, setCapsuleState] =
    useState<DropdownCapsuleState>('default');
  const itemsMap = Object.fromEntries(items.map((item) => [item.id, item]));

  useEffect(() => {
    setCapsuleState(disabled ? 'disabled' : 'default');
  }, [disabled]);
  const content = (
    <DropdownMenu.Content
      className={classNames({
        'radix-dropdown-content-full-width': contentWidth === 'full',
      })}
      style={{ zIndex: zIndex ?? getMaxZIndex() + 1 }}
      align="start"
      sideOffset={sideOffset}
      loop
      onCloseAutoFocus={(event) => event.preventDefault()}
    >
      <Paper
        radius
        shadow="md"
      >
        <div
          style={{
            width: contentWidth
              ? contentWidth === 'full'
                ? '100%'
                : `${contentWidth}px`
              : 'initial',
          }}
          className="flex flex-col p-1 gap-2 min-w-52 max-h-[360px] flex-initial overflow-y-auto"
        >
          {items.map((item) => (
            <DropdownFilterItem
              key={item.id}
              id={item.id}
              label={item.label}
              onSelect={onSelect}
              isSelected={item.id === selected}
              disabled={item.disabled}
            />
          ))}
          {onClear && (
            <div>
              <Button
                link
                label="Clear"
                onClick={onClear}
              />
            </div>
          )}
        </div>
      </Paper>
    </DropdownMenu.Content>
  );

  return (
    <DropdownMenu.Root
      onOpenChange={(open) => {
        setCapsuleState(open ? 'open' : 'default');
      }}
    >
      <DropdownMenu.Trigger
        disabled={disabled}
        className={capsuleWidth === 'full' ? 'w-full' : 'initial'}
      >
        <FilterCapsule
          width={capsuleWidth}
          rounded={rounded}
          size={size}
          leftIcon={leftIcon}
          capsuleState={capsuleState}
          placeholder={placeholder}
          label={selected && itemsMap[selected]?.label}
        />
      </DropdownMenu.Trigger>
      {withoutPortal ? (
        content
      ) : (
        <DropdownMenu.Portal>{content}</DropdownMenu.Portal>
      )}
    </DropdownMenu.Root>
  );
}
